import { ComposerAttachButton } from "./AttachButton";
import { AttachmentStrip } from "./AttachmentStrip";
import { ComposerBar } from "./Bar";
import { CadAnalysisModal } from "./CadAnalysisModal";
import { ComposerCharCount } from "./CharCount";
import { ComposerChipBar } from "./ChipBar";
import { ComposerChipItem } from "./ChipItem";
import { ConnectAreaPicker } from "./ConnectAreaPicker";
import { ConnectorPicker } from "./ConnectorPicker";
import { DeepModePill } from "./DeepModePill";
import { ComposerDropOverlay } from "./DropOverlay";
import { ComposerErrorBanner } from "./ErrorBanner";
import { FileChipList } from "./FileChipList";
import { FocusSelector } from "./FocusSelector";
import { FolderPicker } from "./FolderPicker";
import { ComposerHeader } from "./Header";
import { MentionAutocomplete } from "./MentionAutocomplete";
import { MentionSuggestionList } from "./MentionSuggestionList";
import { ModeSelector } from "./ModeSelector";
import { RichTextEditorLazy } from "./RichTextEditorLazy";
/**
 * Composer compound — public barrel.
 *
 * Public surface:
 *   import { Composer } from "@ando/ui";
 *   <Composer.Root onSubmit={…}>
 *     <Composer.Header modes={…} activeModeId={…} onModeChange={…} />
 *     <Composer.Textarea value={…} onChange={…} />
 *     <Composer.ChipBar chips={chips} onChipDismiss={…}>
 *       <Composer.AttachButton onAttach={…} capabilities={…} onCapabilityToggle={…} />
 *     </Composer.ChipBar>
 *     <Composer.Bar right={<Composer.SendButton canSend={…} streaming={…} onStop={…} />} />
 *   </Composer.Root>
 *
 * Sub-components live in dedicated files under `./composer/` to keep each
 * under 200 LOC and easy to review independently. Types shared between them
 * live in `./types.ts`.
 *
 * May 2026 — v1 parity additions: SlashCommandMenu, VoiceButton,
 * ErrorBanner, CharCount.
 */
import { ComposerRoot } from "./Root";
import { SearchPowerSelector } from "./SearchPowerSelector";
import { ComposerSendButton } from "./SendButton";
import { SlashCommandMenu } from "./SlashCommandMenu";
import { SourceScopePicker } from "./SourceScopePicker";
import { ComposerTextarea } from "./Textarea";
import { ToolsMenu } from "./ToolsMenu";
import { ComposerVoiceButton } from "./VoiceButton";

export const Composer = {
  Root: ComposerRoot,
  Header: ComposerHeader,
  /**
   * @deprecated since May 2026 — prefer ``Composer.RichEditor`` which
   * renders mentions as first-class chip nodes and unifies plain-text +
   * mentions extraction. Kept as an alias for backward compatibility.
   */
  Textarea: ComposerTextarea,
  RichEditor: RichTextEditorLazy,
  ChipBar: ComposerChipBar,
  ChipItem: ComposerChipItem,
  Bar: ComposerBar,
  AttachButton: ComposerAttachButton,
  SendButton: ComposerSendButton,
  FocusSelector,
  DeepModePill,
  /** @deprecated May 26, 2026 — collapsed into ``Composer.DeepModePill``.
   * Kept exported so external consumers don't break during the rollout
   * window; new code should use the pill. */
  SearchPowerSelector,
  ModeSelector,
  ConnectorPicker,
  FolderPicker,
  SourceScopePicker,
  ConnectAreaPicker,
  ToolsMenu,
  AttachmentStrip,
  FileChipList,
  SlashCommandMenu,
  MentionAutocomplete,
  MentionSuggestionList,
  VoiceButton: ComposerVoiceButton,
  ErrorBanner: ComposerErrorBanner,
  CharCount: ComposerCharCount,
  DropOverlay: ComposerDropOverlay,
  CadAnalysisModal,
};

export type { ComposerRootProps } from "./Root";
export type { ComposerHeaderProps } from "./Header";
export type { ComposerTextareaProps } from "./Textarea";
export type { ComposerChipBarProps } from "./ChipBar";
export type { ComposerChipItemProps } from "./ChipItem";
export type { ComposerBarProps } from "./Bar";
export type { ComposerAttachButtonProps } from "./AttachButton";
export type { ComposerSendButtonProps } from "./SendButton";
export type { FocusSelectorProps, ComposerFocus } from "./FocusSelector";
export type {
  SearchPowerSelectorProps,
  ComposerSearchPower,
} from "./SearchPowerSelector";
export type { DeepModePillProps } from "./DeepModePill";
export type { ModeSelectorProps } from "./ModeSelector";
export type { ConnectorPickerProps, ConnectorOption } from "./ConnectorPicker";
export type {
  FolderPickerProps,
  FolderPickerItem,
  FolderPickerProvider,
} from "./FolderPicker";
export type {
  SourceScopePickerProps,
  ScopeFolderItem,
  ScopeFolderProvider,
  ScopeDocumentItem,
} from "./SourceScopePicker";
export type {
  ConnectAreaPickerProps,
  ConnectAreaOption,
} from "./ConnectAreaPicker";
export type { ToolsMenuProps } from "./ToolsMenu";
export type {
  AttachmentStripProps,
  AttachmentImage,
} from "./AttachmentStrip";
export type { FileChipListProps, FileChip } from "./FileChipList";
export type {
  SlashCommand,
  SlashCommandMenuProps,
} from "./SlashCommandMenu";
export type {
  MentionAutocompleteProps,
  MentionAutocompleteResult,
  MentionAutocompleteIcon,
  MentionAutocompleteKind,
} from "./MentionAutocomplete";
export type {
  MentionSuggestionListProps,
  MentionSuggestionListHandle,
} from "./MentionSuggestionList";
export type {
  RichTextEditorProps,
  TiptapMention,
} from "./RichTextEditor";
export type { ComposerVoiceButtonProps } from "./VoiceButton";
export type { ComposerErrorBannerProps } from "./ErrorBanner";
export type { ComposerCharCountProps } from "./CharCount";
export type { ComposerDropOverlayProps } from "./DropOverlay";
export type {
  CadAnalysisModalProps,
  CadModalFile,
  CadSpatialFilterResult,
  CadFeatureFilterResult,
} from "./CadAnalysisModal";
export { COMPOSER_FOCUS_OPTIONS } from "./FocusSelector";
export { COMPOSER_SEARCH_POWER_OPTIONS } from "./SearchPowerSelector";
export type {
  ComposerChip,
  ComposerMode,
  ComposerCapability,
  ComposerAttachKind,
} from "./types";
